import { useEffect, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "@/components/portal/Toast"
import { useVorgehen, type VorgehenEintrag } from "@/components/portal/VorgehenChip"

/**
 * „Spielregeln — so läuft es bei …“ im Kontext-Center (#83), eigener Reiter.
 *
 * Links die gelernte Historie je Anfrageart (90 Tage), rechts der Satz, den
 * die Mitarbeiter im Tooltip an der Mail lesen sollen. Wer hier nichts
 * einträgt, bekommt trotzdem die Zahlen.
 *
 * Paul, 14.09.2026: (1) „So machen wir das“ soll aus den Daten vorbefüllt
 * sein — dafür die Vorschläge (grau unter dem Feld, „Übernehmen“ macht sie
 * zur Regel). (2) Die Tabelle war ewig lang — Anfragearten mit wenigen
 * Fällen und ohne Satz sind eingeklappt.
 */
type Regel = { kategorie: string; gewerk: string; text: string }
const key = (k: string, g: string) => `${k.toLowerCase()}|${(g || "").toLowerCase()}`
/** Unter so vielen Fällen ist eine Anfrageart ohne Satz eingeklappt. */
const WENIG = 3

export function SpielregelnCard({ tenant, onSave }: {
  tenant: string
  onSave: (regeln: Regel[]) => Promise<void>
}) {
  const qc = useQueryClient()
  const q = useVorgehen(tenant)
  const [texte, setTexte] = useState<Record<string, string>>({})
  const [neuKat, setNeuKat] = useState("")
  const [neuText, setNeuText] = useState("")
  const [zusatz, setZusatz] = useState<{ kategorie: string; gewerk: string }[]>([])
  const [speichert, setSpeichert] = useState(false)
  const [erzeugt, setErzeugt] = useState(false)
  const [alle, setAlle] = useState(false)

  useEffect(() => {
    if (!q.data) return
    const t: Record<string, string> = {}
    for (const r of q.data.regeln) t[key(r.kategorie, r.gewerk)] = r.text
    setTexte(t); setZusatz([])
  }, [q.data])

  const zeilen: VorgehenEintrag[] = useMemo(() => {
    const basis = q.data?.eintraege ?? []
    const vorhanden = new Set(basis.map((e) => key(e.kategorie, e.gewerk)))
    const extra = zusatz.filter((z) => !vorhanden.has(key(z.kategorie, z.gewerk)))
      .map((z) => ({ ...z, n: 0, fakten: [], ueblich: "", regel: null, vorschlag: null }))
    return [...basis, ...extra]
  }, [q.data, zusatz])

  // Eine Zeile zählt als „wichtig“, wenn sie genug Fälle hat oder schon
  // einen Satz (Regel, Eingabe oder Vorschlag) trägt. Der Rest klappt ein.
  const wichtig = (z: VorgehenEintrag) =>
    z.n >= WENIG || !!(texte[key(z.kategorie, z.gewerk)] || "").trim() || !!z.regel || !!z.vorschlag
  const sichtbar = alle ? zeilen : zeilen.filter(wichtig)
  const versteckt = zeilen.length - sichtbar.length

  const offeneVorschlaege = zeilen.filter((z) => z.vorschlag && !(texte[key(z.kategorie, z.gewerk)] || "").trim())

  const speichern = async () => {
    setSpeichert(true)
    try {
      const regeln: Regel[] = zeilen
        .map((z) => ({ kategorie: z.kategorie, gewerk: z.gewerk, text: (texte[key(z.kategorie, z.gewerk)] || "").trim() }))
        .filter((r) => r.text)
      await onSave(regeln)
      qc.invalidateQueries({ queryKey: ["vorgehen", tenant] })
      toast("✓ Spielregeln gespeichert")
    } catch (e) {
      toast("Speichern fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally { setSpeichert(false) }
  }

  const vorschlaegeErzeugen = async () => {
    setErzeugt(true)
    try {
      const r = await fetch(`/api/v1/context/vorgehen/vorschlaege?tenant=${encodeURIComponent(tenant)}`, {
        method: "POST", credentials: "include",
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json() as { anzahl: number; vom_modell: number }
      await qc.invalidateQueries({ queryKey: ["vorgehen", tenant] })
      toast(`✓ ${d.anzahl} Vorschläge erzeugt${d.vom_modell < d.anzahl ? ` (${d.anzahl - d.vom_modell} nur aus den Zahlen)` : ""}`)
    } catch (e) {
      toast("Vorschläge fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally { setErzeugt(false) }
  }

  const uebernehmen = (z: VorgehenEintrag) => {
    if (!z.vorschlag) return
    setTexte((t) => ({ ...t, [key(z.kategorie, z.gewerk)]: z.vorschlag! }))
  }

  const stand = q.data?.vorschlaege_stand
    ? new Date(q.data.vorschlaege_stand).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
    : null

  const inputCls = {
    width: "100%", fontSize: 12.5, padding: "6px 8px", border: "1px solid var(--border-color)",
    borderRadius: 6, background: "var(--bg-card)", color: "var(--text)", outline: "none",
  } as const
  const linkBtn = {
    background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, color: "#3C3489", fontWeight: 500,
  } as const

  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-color)", borderRadius: 12, padding: 18 }}>
      <div style={{ fontSize: 15, fontWeight: 600 }}>
        Spielregeln — so läuft es bei {q.data?.tenant_name || tenant}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4, marginBottom: 12, lineHeight: 1.5 }}>
        Je Anfrageart: was in den letzten {q.data?.tage ?? 90} Tagen tatsächlich passiert ist, und daneben der Satz,
        den die Mitarbeiter im Tooltip an der Mail lesen sollen („So machen wir das“). Der Tooltip hängt am
        Kategorie-Chip in der Mail-Liste und in der Mail-Ansicht.
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12, fontSize: 12.5 }}>
        <button
          type="button" className="ev2-hero-ibtn" disabled={erzeugt || !q.data} onClick={vorschlaegeErzeugen}
        >{erzeugt ? "Erzeugt …" : stand ? "Vorschläge neu erzeugen" : "Vorschläge aus der Historie erzeugen"}</button>
        <span style={{ color: "var(--text-muted)" }}>
          {stand ? `Stand ${stand} · ` : ""}ein Modellaufruf je Betrieb, unter einem Cent. Vorschläge stehen grau unter dem Feld und werden erst mit „Übernehmen“ und Speichern zur Regel.
        </span>
        {offeneVorschlaege.length > 0 && (
          <button
            type="button" style={linkBtn}
            onClick={() => setTexte((t) => {
              const n = { ...t }
              for (const z of offeneVorschlaege) n[key(z.kategorie, z.gewerk)] = z.vorschlag!
              return n
            })}
          >Alle {offeneVorschlaege.length} Vorschläge übernehmen</button>
        )}
      </div>

      {q.isLoading && <div style={{ color: "var(--text-muted)", fontSize: 12.5 }}>Lade Historie …</div>}
      {q.isError && <div style={{ color: "var(--danger-color)", fontSize: 12.5 }}>Historie konnte nicht geladen werden.</div>}
      {q.data && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--text-muted)" }}>
                <th style={{ padding: "6px 8px 6px 0", fontWeight: 500 }}>Anfrageart</th>
                <th style={{ padding: "6px 8px", fontWeight: 500, whiteSpace: "nowrap" }}>Fälle</th>
                <th style={{ padding: "6px 8px", fontWeight: 500 }}>Üblich (gelernt)</th>
                <th style={{ padding: "6px 0 6px 8px", fontWeight: 500, width: "44%" }}>So machen wir das</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.length === 0 && (
                <tr><td colSpan={4} style={{ padding: 8, color: "var(--text-muted)" }}>Noch keine kategorisierten Mails in diesem Zeitraum.</td></tr>
              )}
              {sichtbar.map((z) => {
                const k = key(z.kategorie, z.gewerk)
                const leer = !(texte[k] || "").trim()
                return (
                  <tr key={k} style={{ borderTop: "1px solid var(--border-color)", verticalAlign: "top" }}>
                    <td style={{ padding: "8px 8px 8px 0", whiteSpace: "nowrap" }}>
                      <span style={{ fontWeight: z.gewerk ? 400 : 500 }}>{z.kategorie}</span>
                      {z.gewerk && <span style={{ color: "var(--text-muted)" }}> · {z.gewerk}</span>}
                    </td>
                    <td style={{ padding: "8px" }}>{z.n}</td>
                    <td style={{ padding: "8px", color: z.ueblich ? "var(--text)" : "var(--text-muted)" }}>
                      {z.ueblich || (z.n ? "meist keine Aktion nötig" : "—")}
                    </td>
                    <td style={{ padding: "8px 0 8px 8px" }}>
                      <textarea
                        rows={2}
                        aria-label={`So machen wir das: ${z.kategorie}${z.gewerk ? ` · ${z.gewerk}` : ""}`}
                        value={texte[k] || ""}
                        placeholder={z.vorschlag ? "" : "z.B. Zwei Termine vorschlagen, Arman per Aufgabe informieren."}
                        onChange={(e) => setTexte((t) => ({ ...t, [k]: e.target.value }))}
                        style={{ ...inputCls, resize: "vertical", fontFamily: "inherit" }}
                      />
                      {z.vorschlag && leer && (
                        <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginTop: 4, fontSize: 12, color: "var(--text-muted)" }}>
                          <span style={{ fontStyle: "italic", flex: 1 }}>Vorschlag: {z.vorschlag}</span>
                          <button
                            type="button" style={linkBtn}
                            aria-label={`Vorschlag übernehmen: ${z.kategorie}${z.gewerk ? ` · ${z.gewerk}` : ""}`}
                            onClick={() => uebernehmen(z)}
                          >Übernehmen</button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {versteckt > 0 && (
            <button type="button" style={{ ...linkBtn, marginTop: 10 }} onClick={() => setAlle(true)}>
              {versteckt} weitere Anfragearten mit weniger als {WENIG} Fällen anzeigen
            </button>
          )}
          {alle && zeilen.length > sichtbar.length - versteckt && zeilen.some((z) => !wichtig(z)) && (
            <button type="button" style={{ ...linkBtn, marginTop: 10 }} onClick={() => setAlle(false)}>
              Nur Anfragearten mit Fällen oder Satz anzeigen
            </button>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 12, flexWrap: "wrap" }}>
        <input
          type="text" value={neuKat} placeholder="Weitere Anfrageart, z.B. 'Bewerbung'"
          aria-label="Neue Anfrageart"
          onChange={(e) => setNeuKat(e.target.value)} style={{ ...inputCls, width: 220 }}
        />
        <input
          type="text" value={neuText} placeholder="So machen wir das …"
          aria-label="Regel für neue Anfrageart"
          onChange={(e) => setNeuText(e.target.value)} style={{ ...inputCls, flex: 1, minWidth: 220 }}
        />
        <button
          type="button"
          className="ev2-hero-ibtn"
          disabled={!neuKat.trim() || !neuText.trim()}
          onClick={() => {
            const kat = neuKat.trim()
            setZusatz((z) => [...z, { kategorie: kat, gewerk: "" }])
            setTexte((t) => ({ ...t, [key(kat, "")]: neuText.trim() }))
            setNeuKat(""); setNeuText("")
          }}
        >+ Hinzufügen</button>
        <button
          type="button" className="ev2-hero-ibtn ev2-hero-ibtn-primary" style={{ marginLeft: "auto" }}
          disabled={speichert || !q.data} onClick={speichern}
        >{speichert ? "Speichert …" : "Speichern"}</button>
      </div>
    </div>
  )
}
